/** @module store/reporters */

import * as actions from '@/store/modules/reporters/actions'
import * as getters from '@/store/modules/reporters/getters'
import * as mutations from '@/store/modules/reporters/mutations'

const state = () => {
  return {}
}

export const MEDIUM_FLAG = {
  asEmail: 1,
  asFcm: 2
}

/**
 * A Reporter data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {integer} params.id is the ReporterId
 * @param {integer} params.emsId is the referenced emsId of the reporter
 * @param {integer} params.groupId is the referenced groupId of the reporter
 * @param {string} params.name is the Name of the Reporter
 * @param {string} params.type is the Type of the Reporter
 * @param {boolean} params.notifyOnConditionNoLongerFulfilled send trigger when untriggered again
 * @param {boolean} params.disabled states if Reporter is disabled
 * @param {object} params.currentStatus { emsId => 'CONDITION_FULFILLED' || 'CONDITION_VIOLATED' }
 * @param {boolean} params.subscribed describes if current user is subscribed to the reporter
 * @param {object} params.config set parameters of the Reporter
 * @param {object} params.users {userId => MEDIUM_FLAG}
 *
 */
export function Reporter({
  id,
  emsId = null,
  groupId = null,
  name = '',
  type = 'sensor',
  notifyOnConditionNoLongerFulfilled = false,
  disabled = false,
  currentStatus = null,
  subscribed = undefined,
  config = {},
  users = {}
}) {
  this.id = id
  this.emsId = emsId
  this.groupId = groupId
  this.name = name
  this.type = type
  this.notifyOnConditionNoLongerFulfilled = notifyOnConditionNoLongerFulfilled
  this.disabled = disabled
  this.subscribed = subscribed
  this.currentStatus = currentStatus
  this.config = config
  this.users = users
}

/**
 * A ReporterConfig data-struct.
 *
 * @constructor
 *
 * @param {object} params
 * @param {integer} params.probingWindow >= 600 (seconds)
 *
 * for SensorProber:
 * @param {string} params.sensorId
 * @param {string} params.measurementKind {avg, power, energy}
 * @param {string} params.tResolution {1 minute, 15 minutes, 30 minutes, 1 hour}
 * @param {string} params.conditionForTrigger {greater, geq, less, leq, in}
 * @param Array{Float} params.threshold 2 numbers for `in`, 1 for other conditionForTrigger
 *
 * for LogEventReporter:
 * @param {string[]} params.devices ['LD_XXX_']
 * @param {string} params.msgMatcherForTrigger regExp
 * @param {boolean} params.msgNotOkThenTrigger if true => msgMatcherForTrigger ignored
 * @param {boolean} params.probeAllDevices
 *
 */
export function ReporterConfig({
  probingWindow = null,
  fulfillFor = 'ALL',
  sensorId = null,
  measurementKind = null,
  tResolution = null,
  conditionForTrigger = 'greater',
  threshold = null,
  fulfillForDevices = null,
  devices = null,
  msgMatcherForTrigger = null,
  msgNotOkThenTrigger = true,
  probeAllDevices = true
}) {
  this.probingWindow = probingWindow
  this.fulfillFor = fulfillFor

  // SensorReporter
  this.sensorId = sensorId
  this.measurementKind = measurementKind
  this.tResolution = tResolution
  this.conditionForTrigger = conditionForTrigger
  this.threshold = threshold

  // LogEventReporter
  if (typeof devices === 'string') {
    this.devices = devices.split(',')
  } else {
    this.devices = devices
  }
  this.fulfillForDevices = fulfillForDevices
  this.msgMatcherForTrigger = msgMatcherForTrigger
  this.msgNotOkThenTrigger = msgNotOkThenTrigger
  this.probeAllDevices = probeAllDevices
}

export const GENERIC_REPORTER_CONFIG_FIELDS = ['fulfillFor', 'probingWindow']

export const SENSOR_REPORTER_CONFIG_FIELDS = [
  'sensorId',
  'measurementKind',
  'tResolution',
  'conditionForTrigger',
  'threshold'
]

export const LOG_EVENT_REPORTER_CONFIG_FIELDS = [
  'device',
  'fulfillForDevices',
  'msgMatcherForTrigger',
  'msgNotOkThenTrigger'
]

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
